import { useState, useEffect, useCallback, useMemo } from 'react'
import { cn } from '../../lib/utils.js'
import { ScheduleTodayPanel }       from './panels/ScheduleTodayPanel'
import { UpcomingEventsPanel }      from './panels/UpcomingEventsPanel'
import { ChoresTasksPanel }         from './panels/ChoresTasksPanel'
import { MealPlanPanel }            from './panels/MealPlanPanel'
import { AnnouncementsPanel }       from './panels/AnnouncementsPanel'
import { CustodyTransitionsPanel }  from './panels/CustodyTransitionsPanel'
import { WeatherPanel }             from './panels/WeatherPanel'
import { UpcomingBillsPanel }       from './panels/UpcomingBillsPanel'
import { BirthdaysPanel }           from './panels/BirthdaysPanel'
import { FamilyFeedPanel }          from './panels/FamilyFeedPanel'
import { PersonalViewWrapper }      from './personal/PersonalViewWrapper'
import { useInstallPrompt }         from './useInstallPrompt'
import { useFullscreen }            from './useFullscreen'

const MEMBER_COLORS = {
  slate: '#64748b', rose: '#f43f5e', amber: '#f59e0b', emerald: '#10b981',
  sky: '#0ea5e9', violet: '#8b5cf6', pink: '#ec4899', orange: '#f97316',
  teal: '#14b8a6', indigo: '#6366f1',
}

const PANEL_COMPONENTS = {
  schedule_today:       ScheduleTodayPanel,
  upcoming_events:      UpcomingEventsPanel,
  chores_tasks:         ChoresTasksPanel,
  meal_plan:            MealPlanPanel,
  announcements:        AnnouncementsPanel,
  custody_transitions:  CustodyTransitionsPanel,
  weather:              WeatherPanel,
  upcoming_bills:       UpcomingBillsPanel,
  birthdays:            BirthdaysPanel,
  family_feed:          FamilyFeedPanel,
}

const isIOS =
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)

function useNow() {
  const [now, setNow] = useState(new Date())
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000)
    return () => clearInterval(t)
  }, [])
  return now
}

function PanelProgress({ durationMs, panelKey }) {
  const [width, setWidth] = useState(0)
  useEffect(() => {
    setWidth(0)
    const start = Date.now()
    const raf = () => {
      const pct = Math.min(((Date.now() - start) / durationMs) * 100, 100)
      setWidth(pct)
      if (pct < 100) requestAnimationFrame(raf)
    }
    requestAnimationFrame(raf)
  }, [panelKey, durationMs])
  return (
    <div className="h-0.5 bg-white/10 absolute bottom-0 inset-x-0">
      <div className="h-full bg-white/40 transition-none" style={{ width: `${width}%` }} />
    </div>
  )
}

function InstallBanner({ deferredPrompt, promptInstall, onDismiss }) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 20_000)
    return () => clearTimeout(t)
  }, [onDismiss])

  return (
    <div className="fixed bottom-0 inset-x-0 z-50 bg-[#12122a]/95 backdrop-blur-sm border-t border-white/10 px-4 py-3 flex items-center gap-3">
      {deferredPrompt ? (
        <>
          <svg className="w-5 h-5 text-violet-400 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 16l-4-4m4 4 4-4m-4 4V4"/><path d="M20 21H4"/>
          </svg>
          <p className="text-gray-300 text-sm flex-1">
            <span className="text-white font-semibold">Install DoorChart</span> for automatic fullscreen on every launch
          </p>
          <button
            onClick={promptInstall}
            className="flex-shrink-0 bg-violet-600 hover:bg-violet-500 active:bg-violet-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors"
          >
            Install
          </button>
          <button onClick={onDismiss} className="flex-shrink-0 text-white/30 hover:text-white/70 transition-colors p-0.5">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
          </button>
        </>
      ) : isIOS ? (
        <>
          <svg className="w-5 h-5 text-violet-400 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/>
          </svg>
          <p className="text-gray-300 text-sm flex-1">
            For fullscreen: tap <span className="text-white font-semibold">Share</span> then{' '}
            <span className="text-white font-semibold">Add to Home Screen</span>
          </p>
          <button onClick={onDismiss} className="flex-shrink-0 text-white/30 hover:text-white/70 transition-colors p-0.5">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
          </button>
        </>
      ) : null}
    </div>
  )
}

export function AmbientDisplay({ displayData, deviceToken, isOnline }) {
  const { settings, members = [], announcements = [], custody = [], birthdays = [], wallPosts = [] } = displayData
  const panelConfig = settings?.panel_config ?? []
  const idleTimeout = (settings?.idle_timeout_seconds ?? 120) * 1000

  const [panelIdx,        setPanelIdx]        = useState(0)
  const [isPaused,        setIsPaused]         = useState(false)
  const [panelKey,        setPanelKey]         = useState(0)
  const [activeMember,    setActiveMember]     = useState(null)
  const [drawerOpen,      setDrawerOpen]       = useState(false)
  const [bannerDismissed, setBannerDismissed]  = useState(false)

  const { deferredPrompt, isInstalled, promptInstall } = useInstallPrompt()
  const { isFullscreen, enter: enterFs, exit: exitFs, supported: fsSupported } = useFullscreen()

  const isDisplayModeFull = useMemo(() => window.matchMedia('(display-mode: fullscreen)').matches, [])
  const showFsBtn  = fsSupported && !isDisplayModeFull
  const showBanner = !isInstalled && !bannerDismissed && (deferredPrompt !== null || isIOS)

  const now = useNow()

  const enabledPanels = useMemo(() => {
    return panelConfig.filter((p) => {
      if (!p.enabled || p.admin_only) return false
      if (p.id === 'announcements' && announcements.length === 0) return false
      if (p.id === 'custody_transitions' && custody.filter((c) => c.is_active).length === 0) return false
      if (p.id === 'weather' && (!settings?.weather_latitude || !settings?.weather_longitude)) return false
      if (p.id === 'birthdays' && birthdays.length === 0) return false
      if (p.id === 'family_feed' && wallPosts.length === 0) return false
      return true
    })
  }, [panelConfig, announcements, custody, settings, birthdays, wallPosts])

  const currentPanel = enabledPanels[panelIdx % enabledPanels.length]
  const duration = (currentPanel?.duration_seconds ?? 30) * 1000

  useEffect(() => {
    if (isPaused || !enabledPanels.length) return
    const timer = setTimeout(() => {
      setPanelIdx((i) => (i + 1) % enabledPanels.length)
      setPanelKey((k) => k + 1)
    }, duration)
    return () => clearTimeout(timer)
  }, [panelIdx, isPaused, enabledPanels, duration])

  const handlePanelTap   = useCallback(() => setIsPaused((p) => !p), [])
  const handleDismissBanner = useCallback(() => setBannerDismissed(true), [])

  const PanelComponent = currentPanel ? PANEL_COMPONENTS[currentPanel.id] : null

  const timeStr = now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
  const dateStr = now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })

  const visibleMembers = members.filter((m) => m.displaySettings?.show_on_display !== false)

  if (activeMember) {
    return (
      <PersonalViewWrapper
        member={activeMember}
        data={displayData}
        deviceToken={deviceToken}
        idleTimeoutMs={idleTimeout}
        onReturn={() => setActiveMember(null)}
      />
    )
  }

  return (
    <div className="fixed inset-0 bg-[#0d0d1a] flex flex-col overflow-hidden select-none">

      {/* Header */}
      <div className="flex items-center justify-between px-4 sm:px-8 py-2 sm:py-4 flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 sm:w-7 sm:h-7 bg-violet-600 rounded-lg flex items-center justify-center text-white text-xs sm:text-sm font-bold">D</div>
          <span className="text-white/40 text-xs sm:text-sm font-semibold tracking-wide hidden sm:block">DoorChart</span>
          {showFsBtn && (
            <button
              onClick={isFullscreen ? exitFs : enterFs}
              className="ml-1 text-white/25 hover:text-white/70 transition-colors p-1"
              title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            >
              {isFullscreen ? (
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"/>
                </svg>
              ) : (
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
                </svg>
              )}
            </button>
          )}
        </div>
        <div className="text-right">
          <div className="text-white text-2xl sm:text-4xl font-bold tabular-nums">{timeStr}</div>
          <div className="text-gray-500 text-xs sm:text-sm">{dateStr}</div>
        </div>
      </div>

      {/* Offline badge */}
      {!isOnline && (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-amber-500/90 text-amber-950 text-xs font-bold px-3 py-1 rounded-full z-10">
          Offline — cached data
        </div>
      )}

      {/* Panel area — takes all remaining height; contains drawer overlay */}
      <div
        className="flex-1 overflow-hidden relative cursor-pointer min-h-0"
        onClick={handlePanelTap}
      >
        {isPaused && (
          <div className="absolute top-3 right-3 z-10 bg-white/10 text-white text-xs font-semibold px-3 py-1.5 rounded-full">
            ⏸ Paused
          </div>
        )}

        <div key={panelKey} className="h-full panel-animate-in">
          {PanelComponent ? (
            PanelComponent === WeatherPanel
              ? <PanelComponent settings={settings} />
              : <PanelComponent data={displayData} />
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-gray-600">No panels configured</p>
            </div>
          )}
        </div>

        {!isPaused && enabledPanels.length > 0 && (
          <PanelProgress durationMs={duration} panelKey={panelKey} />
        )}

        {/* Drawer backdrop — dims content when drawer is open */}
        <div
          className={cn(
            'absolute inset-0 z-20 transition-opacity duration-300 bg-black/60',
            drawerOpen ? 'opacity-100' : 'opacity-0 pointer-events-none',
          )}
          onClick={(e) => { e.stopPropagation(); setDrawerOpen(false) }}
        />

        {/* Member pull-out drawer — slides in from left */}
        {visibleMembers.length > 0 && (
          <div
            className={cn(
              'absolute left-0 top-0 bottom-0 z-30 flex transition-transform duration-300 ease-out',
              drawerOpen ? 'translate-x-0' : '-translate-x-20',
            )}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Drawer panel */}
            <div className="w-20 bg-[#0b0b1d]/95 backdrop-blur-sm border-r border-white/10 flex flex-col items-center pt-3 pb-4 gap-3 overflow-y-auto">
              <p className="text-white/20 text-[9px] uppercase tracking-widest mb-1">Who are you?</p>
              {visibleMembers.map((m) => {
                const color   = MEMBER_COLORS[m.memberColor] ?? '#8b5cf6'
                const initial = m.displaySettings?.avatar_emoji ?? m.displayName?.[0]?.toUpperCase()
                return (
                  <button
                    key={m.id}
                    onClick={() => { setActiveMember(m); setDrawerOpen(false) }}
                    className="flex flex-col items-center gap-1 group"
                  >
                    <div
                      className="w-11 h-11 rounded-full flex items-center justify-center text-white text-base font-bold ring-2 ring-transparent group-hover:ring-white/40 transition-all"
                      style={{ backgroundColor: color }}
                    >
                      {initial}
                    </div>
                    <span className="text-gray-500 text-[10px] font-medium truncate max-w-[4rem] text-center group-hover:text-gray-300 transition-colors">
                      {m.displayName?.split(' ')[0]}
                    </span>
                  </button>
                )
              })}
            </div>

            {/* Tab handle — always visible as the exposed right edge */}
            <button
              onClick={() => setDrawerOpen((o) => !o)}
              className="self-center flex-shrink-0 bg-white/8 hover:bg-white/15 active:bg-white/20 border border-l-0 border-white/10 rounded-r-2xl px-1.5 py-5 flex flex-col items-center justify-center gap-2 transition-colors"
              title={drawerOpen ? 'Close' : 'Family members'}
            >
              {drawerOpen ? (
                <svg className="w-3 h-3 text-white/40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M15 18l-6-6 6-6"/>
                </svg>
              ) : (
                visibleMembers.slice(0, 5).map((m, i) => (
                  <div
                    key={i}
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: MEMBER_COLORS[m.memberColor] ?? '#8b5cf6' }}
                  />
                ))
              )}
            </button>
          </div>
        )}
      </div>

      {/* Install / Add-to-Home-Screen prompt */}
      {showBanner && (
        <InstallBanner
          deferredPrompt={deferredPrompt}
          promptInstall={promptInstall}
          onDismiss={handleDismissBanner}
        />
      )}
    </div>
  )
}
